import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { Profile, Student, Teacher } from '@/types'
import { triggerSessionStart, triggerFirstLogin, triggerStudentRegisters } from '@/lib/flowTrigger'
import { logger } from '@/lib/logger'

interface AuthContextType {
  user: User | null
  session: Session | null
  profile: Profile | null
  student: Student | null
  teacher: Teacher | null
  isInCohort: boolean
  cohortId: string | null
  cohortIds: string[]
  isAccessExpired: boolean
  isSuperProf: boolean
  isLoading: boolean
  isAuthenticated: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signUp: (email: string, password: string, metadata: SignUpMetadata) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>
  refreshProfile: () => Promise<void>
}

interface SignUpMetadata {
  first_name: string
  last_name: string
  phone?: string
  role: 'student' | 'parent'
  birth_date?: string
  age_group?: 'children' | 'adult'
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Helper: envolver una query con timeout
function withTimeout<T>(fn: () => PromiseLike<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    Promise.resolve().then(() => fn()).catch((e) => { throw e }),
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Timeout (${ms}ms) en: ${label}`)), ms)
    )
  ])
}

async function loadStudentRecord(userId: string) {
  // Buscar el row existente. Si hay timeout/RLS, NO crear duplicado:
  // se devuelve null y el caller maneja la ausencia. La creación
  // automática ocurre solo si confirmamos que no hay row.
  try {
    const { data: studentData, error: selectError } = await withTimeout(
      () => supabase.from('students').select('*').eq('profile_id', userId).maybeSingle(),
      10000, 'query students'
    )
    if (studentData) return studentData
    if (selectError) {
      console.warn('⚠️ Error leyendo student record (no se crea duplicado):', selectError.message)
      return null
    }
  } catch (e) {
    console.warn('⚠️ Timeout/error leyendo student record (no se crea duplicado):', e)
    return null
  }

  // SELECT confirmó 0 rows → crear con upsert para que la unique constraint
  // (students.profile_id) prevenga duplicados aunque dos requests corran a la vez.
  try {
    const { data: newStudent, error: upsertError } = await withTimeout(
      () => supabase.from('students').upsert({
        profile_id: userId,
        birth_date: new Date().toISOString().split('T')[0],
        age_group: 'adult'
      } as any, { onConflict: 'profile_id' }).select().maybeSingle(),
      10000, 'upsert student'
    )
    if (upsertError) console.warn('⚠️ No se pudo crear student automáticamente:', upsertError.message)
    return newStudent
  } catch (e) {
    console.warn('⚠️ Error creando student record:', e)
    return null
  }
}

async function loadCohortData(userId: string) {
  try {
    const cohortResult = await withTimeout(
      () => (supabase.from('cohorts') as any)
        .select('id')
        .eq('is_active', true)
        .filter('student_ids', 'cs', `{"${userId}"}`),
      10000, 'query cohorts'
    ) as any
    const allIds = cohortResult?.data?.map((c: any) => c.id) || []
    return { isInCohort: allIds.length > 0, cohortId: allIds[0] || null, cohortIds: allIds }
  } catch (e) {
    console.warn('⚠️ Error verificando cohorte:', e)
    return { isInCohort: false, cohortId: null, cohortIds: [] }
  }
}

async function checkAccessExpiry(userId: string): Promise<boolean> {
  try {
    const [{ data: lastUnitExam }, { data: lastFinalExam }] = await Promise.all([
      (supabase.from('exam_attempts') as any)
        .select('completed_at').eq('student_id', userId)
        .not('completed_at', 'is', null).order('completed_at', { ascending: false }).limit(1),
      (supabase.from('final_exam_attempts') as any)
        .select('completed_at').eq('student_id', userId)
        .not('completed_at', 'is', null).order('completed_at', { ascending: false }).limit(1)
    ]) as any[]

    const candidateDates: Date[] = []
    if (lastUnitExam?.[0]?.completed_at) candidateDates.push(new Date(lastUnitExam[0].completed_at))
    if (lastFinalExam?.[0]?.completed_at) candidateDates.push(new Date(lastFinalExam[0].completed_at))

    if (candidateDates.length === 0) return false
    const lastExamDate = new Date(Math.max(...candidateDates.map(d => d.getTime())))
    const expiresAt = new Date(lastExamDate)
    expiresAt.setMonth(expiresAt.getMonth() + 1)
    return new Date() > expiresAt
  } catch (e) {
    console.warn('⚠️ Error verificando expiración de acceso:', e)
    return false
  }
}

async function loadTeacherRecord(userId: string) {
  const { data: teacherData } = await withTimeout(
    () => supabase.from('teachers').select('*').eq('profile_id', userId).maybeSingle(),
    10000, 'query teachers'
  )
  return teacherData
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [student, setStudent] = useState<Student | null>(null)
  const [teacher, setTeacher] = useState<Teacher | null>(null)
  const [isInCohort, setIsInCohort] = useState(false)
  const [cohortId, setCohortId] = useState<string | null>(null)
  const [cohortIds, setCohortIds] = useState<string[]>([])
  const [isAccessExpired, setIsAccessExpired] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Cargar perfil del usuario - retorna true si se cargó correctamente
  const loadProfile = async (userId: string): Promise<boolean> => {
  try {
    console.log('🔍 Cargando perfil para userId:', userId)
    console.log('📡 Consultando tabla profiles...')

    const profileResult = await withTimeout(
      () => supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
      10000, 'query profiles'
    )
    const profileData = profileResult.data as Record<string, any> | null
    const profileError = profileResult.error
    console.log('📡 Respuesta profiles:', { data: !!profileData, error: profileError?.message })

    if (profileError) { console.error('❌ Error cargando perfil:', profileError.message); return false }
    if (!profileData) { console.log('⚠️ Perfil no encontrado para userId:', userId); return false }

    console.log('✅ Perfil cargado:', profileData.email, '- rol:', profileData.role)
    setProfile(profileData as Profile)

    if (profileData.role === 'student' || profileData.role === 'parent') {
      const studentData = await loadStudentRecord(userId)
      setStudent(studentData as Student | null)

      const cohort = await loadCohortData(userId)
      setIsInCohort(cohort.isInCohort)
      setCohortId(cohort.cohortId)
      setCohortIds(cohort.cohortIds)

      const expired = await checkAccessExpiry(userId)
      setIsAccessExpired(expired)

    } else if (profileData.role === 'teacher') {
      const teacherData = await loadTeacherRecord(userId)
      setTeacher(teacherData as Teacher | null)
    }

    return true
  } catch (error) {
    console.error('❌ Error cargando perfil:', error instanceof Error ? error.message : error)
    return false
  }
}

  // Inicializar sesión
  useEffect(() => {
    const initSession = async () => {
      try {
        logger.log('🚀 Inicializando sesión...')
        const { data: { session } } = await supabase.auth.getSession()

        if (session) {
          logger.log('🔑 Sesión existente encontrada para:', session.user.email)
          setSession(session)
          setUser(session.user)
          await loadProfile(session.user.id)
        } else {
          logger.log('ℹ️ No hay sesión activa')
        }
      } catch (error) {
        logger.error('Error initializing session:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initSession()

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        logger.log('🔄 Auth state change:', _event, session?.user?.email)
        setSession(session)
        setUser(session?.user ?? null)

        if (session?.user) {
          // NO usar await aquí - dejar que se resuelva asincrónicamente
          // para no bloquear el handler de auth
          loadProfile(session.user.id).then(loaded => {
            logger.log('📋 Perfil cargado desde onAuthStateChange:', loaded)
            setIsLoading(false)
          }).catch(err => {
            logger.error('❌ Error en loadProfile desde onAuthStateChange:', err)
            setIsLoading(false)
          })
        } else {
          setProfile(null)
          setStudent(null)
          setTeacher(null)
          setIsInCohort(false)
          setIsAccessExpired(false)
          setIsLoading(false)
        }
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Iniciar sesión
  const signIn = async (email: string, password: string) => {
    try {
      logger.log('🔐 Intentando login para:', email)
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) {
        logger.error('❌ Error en signIn:', error.message)
      } else {
        logger.log('✅ signIn exitoso')
        // Trigger de inicio de sesión (fire-and-forget, después del login exitoso)
        const { data: { session: s } } = await supabase.auth.getSession()
        if (s?.user) {
          // Verificar role antes de disparar
          const { data: p } = await supabase.from('profiles').select('role').eq('id', s.user.id).maybeSingle() as { data: { role: string } | null }
          if (p?.role === 'student') {
            triggerSessionStart(s.user.id)
            // Verificar si es primer login
            if (s.user.last_sign_in_at === s.user.created_at || !s.user.last_sign_in_at) {
              triggerFirstLogin(s.user.id)
            }
          }
        }
      }

      return { error }
    } catch (error) {
      return { error: error as Error }
    }
  }

  // Registrar usuario
  const signUp = async (email: string, password: string, metadata: SignUpMetadata) => {
    try {
      logger.log('📝 Iniciando registro para:', email)
      logger.log('   Metadata:', metadata)

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      })

      if (error) {
        logger.error('❌ Error en signUp:', error.message)
        return { error }
      }

      logger.log('✅ Usuario registrado:', data.user?.id)
      logger.log('   Session:', data.session ? 'Activa' : 'Requiere confirmación de email')

      // Si no hay sesión, significa que requiere confirmación de email
      if (!data.session) {
        logger.log('⚠️ Se requiere confirmación de email antes de poder iniciar sesión')
        return {
          error: new Error('Por favor revisa tu email para confirmar tu cuenta antes de iniciar sesión.')
        }
      }

      // Esperar a que el trigger cree el perfil, con reintentos
      if (data.user) {
        logger.log('⏳ Esperando que el trigger cree el perfil...')

        let profileLoaded = false

        // Intentar 3 veces con delays crecientes (1s, 2s, 3s)
        for (let attempt = 1; attempt <= 3; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt))
          profileLoaded = await loadProfile(data.user.id)
          if (profileLoaded) {
            logger.log(`✅ Perfil cargado en intento ${attempt}`)
            break
          }
          logger.log(`⏳ Intento ${attempt}/3 - perfil aún no disponible`)
        }

        if (!profileLoaded) {
          logger.warn('⚠️ Perfil no se pudo cargar, pero el usuario fue creado correctamente')
        }

        // Trigger de registro (fire-and-forget)
        if (metadata.role === 'student') {
          triggerStudentRegisters(data.user.id)
        }
      }

      return { error: null }
    } catch (error) {
      logger.error('❌ Error inesperado en signUp:', error)
      return { error: error as Error }
    }
  }

  // Cerrar sesión
  const signOut = async () => {
    // Limpiar estado local PRIMERO (no depender de que signOut() funcione)
    setProfile(null)
    setStudent(null)
    setTeacher(null)
    setSession(null)
    setUser(null)
    setIsInCohort(false)
    setIsAccessExpired(false)
    setCohortId(null)
    setCohortIds([])
    try {
      await supabase.auth.signOut()
    } catch (err) {
      logger.warn('Error en signOut (sesión limpiada localmente):', err)
    }
  }

  // Actualizar perfil
  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error('No user logged in') }

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates as never)
        .eq('id', user.id)

      if (!error) {
        setProfile(prev => prev ? { ...prev, ...updates } : null)
      }

      return { error }
    } catch (error) {
      return { error: error as Error }
    }
  }

  // Refrescar perfil
  const refreshProfile = async () => {
    if (user) {
      await loadProfile(user.id)
    }
  }

  const value: AuthContextType = {
    user,
    session,
    profile,
    student,
    teacher,
    isInCohort,
    cohortId,
    cohortIds,
    isAccessExpired,
    isSuperProf: !!(profile?.role === 'teacher' && profile?.is_super_prof),
    isLoading,
    isAuthenticated: !!user && !!profile,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
